import { useCallback, useEffect, useRef, type ReactEventHandler } from "react";
import { useUiStore } from "@/store/uiStore";

/**
 * Hosts whose sign-in pages refuse to be embedded in an iframe
 * (they send `X-Frame-Options: deny` / CSP `frame-ancestors 'none'`).
 *
 * The embedded Chatwoot iframe drives OAuth by doing `window.location.href =
 * <provider url>` on itself (e.g. the "Sign in with Microsoft" button when
 * creating a new email inbox). Inside the frame that request is blocked and the
 * user sees a blank/"refused to connect" panel.
 */
const FRAME_BLOCKING_OAUTH_HOSTS = [
  "login.microsoftonline.com",
  "login.microsoft.com",
  "login.live.com",
  "accounts.google.com",
];

function isFrameBlockingOAuthUrl(rawUrl: string): boolean {
  try {
    return FRAME_BLOCKING_OAUTH_HOSTS.includes(new URL(rawUrl).hostname);
  } catch {
    return false;
  }
}

/**
 * The page Chatwoot's OAuth callback redirects to once the mailbox is connected
 * — `…/app/accounts/:id/settings/inboxes/new/:inbox_id/agents` (new inbox) or
 * `…/app/accounts/:id/settings/inboxes/:inbox_id` (re-auth of an existing one).
 * Reaching it in the popup means the flow finished successfully.
 */
function isChatwootInboxUrl(pathname: string): boolean {
  return pathname.includes("/app/") && pathname.includes("/settings/inboxes");
}

// The Navigation API isn't in the TS DOM lib; model just the slice we use.
interface NavigateEventLike extends Event {
  readonly destination: { readonly url: string };
}
interface NavigationLike {
  addEventListener(
    type: "navigate",
    listener: (event: NavigateEventLike) => void,
  ): void;
  removeEventListener(
    type: "navigate",
    listener: (event: NavigateEventLike) => void,
  ): void;
}

const POPUP_FEATURES = "popup,width=600,height=760";
const POLL_INTERVAL_MS = 400;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Wire the returned handler onto the Chatwoot `<iframe>`'s `onLoad`.
 *
 * On every (re)load it binds a Navigation API listener to the same-origin frame
 * that catches the "sign in with <provider>" navigation and, instead of letting
 * the frame (which the provider forbids) or the whole tab go to the provider,
 * runs the round-trip in a popup. The Atlas shell stays put; when Chatwoot's
 * callback lands the popup on the new inbox we close it and deep-link the
 * embedded iframe there — so the user ends up back in Atlas, not on standalone
 * Chatwoot.
 *
 * Safe no-op when the frame is cross-origin (e.g. localhost against the dev
 * domain) or the Navigation API is unavailable.
 */
export function useIframeOAuthBreakout(): ReactEventHandler<HTMLIFrameElement> {
  // The frame window we're bound to plus its detach fn, so a reload rebinds
  // cleanly and we never stack duplicate listeners on the same window.
  const boundRef = useRef<{ nav: NavigationLike; detach: () => void } | null>(
    null,
  );
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const runOAuthPopup = useCallback(
    (url: string) => {
      const popup = window.open(url, "oauthBreakout", POPUP_FEATURES);
      // Popup blocked: fall back to a top-level navigation so the flow still
      // completes — it just lands on standalone Chatwoot rather than back here.
      if (!popup) {
        window.location.assign(url);
        return;
      }

      stopPolling();
      const startedAt = Date.now();
      let leftAtlasOrigin = false;

      pollRef.current = window.setInterval(() => {
        if (popup.closed || Date.now() - startedAt > POLL_TIMEOUT_MS) {
          stopPolling();
          return;
        }

        let pathname: string;
        try {
          // Same-origin read; throws while the popup sits on the provider.
          if (popup.location.href === "about:blank") return;
          pathname = popup.location.pathname;
        } catch {
          leftAtlasOrigin = true; // now on the provider (cross-origin)
          return;
        }

        // Don't react to our own origin until the popup has actually visited
        // the provider, so the initial/blank state can't end the flow early.
        if (!leftAtlasOrigin) return;
        // Chatwoot's callback controller is a transient 302 hop — keep waiting.
        if (pathname.includes("/callback")) return;

        stopPolling();
        const { setChatwootTarget, notify } = useUiStore.getState();
        if (isChatwootInboxUrl(pathname)) {
          // Pull the just-connected inbox into the embedded Chatwoot iframe.
          setChatwootTarget(popup.location.pathname + popup.location.search);
        } else {
          notify(
            "danger",
            "Couldn't finish connecting the mailbox. Please try again.",
          );
        }
        popup.close();
      }, POLL_INTERVAL_MS);
    },
    [stopPolling],
  );

  const detach = useCallback(() => {
    boundRef.current?.detach();
    boundRef.current = null;
  }, []);

  const attach = useCallback(
    (frame: HTMLIFrameElement) => {
      let nav: NavigationLike | undefined;
      try {
        // Same-origin property access; throws across origins.
        nav = (
          frame.contentWindow as unknown as { navigation?: NavigationLike } | null
        )?.navigation;
      } catch {
        nav = undefined;
      }
      if (!nav || boundRef.current?.nav === nav) return;
      detach();

      const onNavigate = (event: NavigateEventLike) => {
        const url = event.destination?.url;
        if (!url || !isFrameBlockingOAuthUrl(url)) return;
        // Keep the iframe where it is; the popup handles the provider round-trip.
        event.preventDefault();
        runOAuthPopup(url);
      };

      nav.addEventListener("navigate", onNavigate);
      boundRef.current = {
        nav,
        detach: () => nav.removeEventListener("navigate", onNavigate),
      };
    },
    [detach, runOAuthPopup],
  );

  useEffect(
    () => () => {
      detach();
      stopPolling();
    },
    [detach, stopPolling],
  );

  return useCallback((event) => attach(event.currentTarget), [attach]);
}
